/**
 * The three things a page can be before it has data: waiting, failed, and
 * unavailable. They are kit Skeleton, Alert and Empty, so every surface that
 * waits or fails looks like every other one.
 */

import type { TemplateInstance } from 'hydrogen/runtime';
import { html } from 'hydrogen/runtime';

import { ActionButton, GLYPH, Icon, LinkButton } from './kit.js';
import { PROJECTS_PATH } from './nav.js';

/** What the person is told to do when the authority has nothing readable for a project. */
export const SYNC_COMMAND = 'matrix sync';

/** Why a project's pages read nothing for an account that is not its member. */
export const NO_PROJECT_MESSAGE =
  'Your account is not a member of this project, so Matrix will not read it. Open one of your own projects from Projects, or ask its owner for an invite.';

/**
 * What a project whose data cannot be read is told. Whatever the transport
 * said (refused, unreachable, silent), the one thing a person can do is the
 * same: the project's certified state reaches matrixbased.com by `matrix sync`.
 */
const NOT_PUBLISHED =
  "This project hasn't been published to matrixbased.com yet. Changes are made with the matrix CLI on your machine; to publish this project's certified state, run:";

/**
 * A page waiting on its first projection: the page's own layout, drawn as
 * skeletons. The heading is real so the outline is complete while the cards
 * are not.
 */
export function LoadingState(options: { title: string; detail: string }): TemplateInstance {
  return html`
    <section class="mx-page" role="status" aria-live="polite" data-state-kind="loading">
      <header class="mx-page-header">
        <div class="mx-page-header__text">
          <h1 class="mx-page-header__title">${options.title}</h1>
          <p class="mx-page-header__description">${options.detail}</p>
        </div>
      </header>
      <div class="mx-metrics" aria-hidden="true">
        <div class="ui-card mx-metric" data-slot="card">
          <div class="ui-skeleton mx-skeleton--label" data-slot="skeleton"></div>
          <div class="ui-skeleton mx-skeleton--value" data-slot="skeleton"></div>
        </div>
        <div class="ui-card mx-metric" data-slot="card">
          <div class="ui-skeleton mx-skeleton--label" data-slot="skeleton"></div>
          <div class="ui-skeleton mx-skeleton--value" data-slot="skeleton"></div>
        </div>
        <div class="ui-card mx-metric" data-slot="card">
          <div class="ui-skeleton mx-skeleton--label" data-slot="skeleton"></div>
          <div class="ui-skeleton mx-skeleton--value" data-slot="skeleton"></div>
        </div>
      </div>
      <div class="ui-card mx-section" data-slot="card" aria-hidden="true">
        <div class="ui-card-content mx-skeleton-rows" data-slot="card-content">
          <div class="ui-skeleton" data-slot="skeleton"></div>
          <div class="ui-skeleton" data-slot="skeleton"></div>
          <div class="ui-skeleton" data-slot="skeleton"></div>
          <div class="ui-skeleton" data-slot="skeleton"></div>
        </div>
      </div>
    </section>
  `;
}

/**
 * What every address answers with before the first projection arrives.
 *
 * A reader who navigates by heading gets the outline and nothing else, and
 * this drew its one line as an h2 under no root at all — so for as long as the
 * connection took, every address in the product was a page with no title.
 */
export function connectingBody(): TemplateInstance {
  return LoadingState({ title: 'Connecting to Matrix', detail: 'Loading the current typed authority projection.' });
}

/** A failure the page cannot work around: a kit destructive Alert with the way forward. */
export function ErrorState(options: {
  id: string;
  title: string;
  detail: string;
  action: TemplateInstance;
}): TemplateInstance {
  return html`
    <section class="mx-state" data-state-kind="error">
      <div class="ui-alert" data-slot="alert" data-variant="destructive" role="alert" aria-labelledby="${options.id}">
        ${Icon(GLYPH.alert)}
        <h1 class="ui-alert-title" data-slot="alert-title" id="${options.id}">${options.title}</h1>
        <div class="ui-alert-description" data-slot="alert-description">${options.detail}</div>
      </div>
      <div class="mx-state__actions">${options.action}</div>
    </section>
  `;
}

/**
 * The project's data is not published here: said once, with the one thing to
 * do. With `detail` the cause is not that (the account is not a member), so
 * the detail is the whole answer and `matrix sync` is not offered.
 */
export function UnavailableCard(options: { detail?: string; projectsLink?: boolean; page?: boolean }): TemplateInstance {
  return html`
    <div class="ui-empty" data-slot="empty" data-region="data-unavailable">
      <div class="ui-empty-header" data-slot="empty-header">
        <div class="ui-empty-icon" data-slot="empty-icon" data-variant="icon">${Icon(GLYPH.database)}</div>
        ${options.page === true
          ? html`<h1 class="ui-empty-title" data-slot="empty-title">Project data isn't available yet</h1>`
          : html`<h2 class="ui-empty-title" data-slot="empty-title">Project data isn't available yet</h2>`}
        <p class="ui-empty-description" data-slot="empty-description">${options.detail ?? NOT_PUBLISHED}</p>
      </div>
      <div class="ui-empty-content" data-slot="empty-content">
        ${options.detail === undefined ? html`<pre class="mx-state__command"><code>${SYNC_COMMAND}</code></pre>` : html``}
        <div class="mx-state__actions">
          ${ActionButton({ action: 'retry', label: 'Try again', variant: 'default' })}
          ${options.projectsLink === false ? html`` : LinkButton({ href: PROJECTS_PATH, label: 'All projects', variant: 'outline' })}
        </div>
      </div>
    </div>
  `;
}

/** The whole page, when the project's data cannot be read. Never "Connecting" with no end. */
export function DataUnavailable(options: { detail?: string }): TemplateInstance {
  return html`
    <section class="mx-state" data-state-kind="unavailable">
      ${UnavailableCard({ ...options, page: true })}
    </section>
  `;
}
